import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { downloadBlobFile, downloadFile } from '@shared/helpers';
import { NotificationService } from '@shared/services/notification.service';
import { Subject, takeUntil, timer } from 'rxjs';
import { SyncMonitoringService } from '../sync-monitoring.service';

export interface IAuditReportDialogData {
	environmentId: string;
	environmentName: string;
}

// How many times to ask for the report while core-one builds it.
const REPORT_POLL_LIMIT = 30;
// The preview shows at most this many characters; Copy and Save use the whole report.
const PREVIEW_MAX_LENGTH = 200_000;

/** The environment's latest audit state as JSON: each table's last completed run with every finding. core-one builds the report on the first request (202 with retryAfter), so the dialog asks again until it gets a presigned URL, then reads the report from it to show, copy and save. */
@Component({
	selector: 'app-audit-report-dialog',
	templateUrl: './audit-report-dialog.component.html',
	styleUrls: ['../audit-dialogs.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class AuditReportDialogComponent implements OnInit, OnDestroy {
	public state: 'building' | 'ready' | 'failed' = 'building';
	/** The translation key of why the report is not shown. */
	public errorKey: string | null = null;
	public url: string | null = null;
	public reportText: string | null = null;
	public preview = '';
	public stats: { tables: number; findings: number; sizeKb: string } | null = null;

	private destroy$ = new Subject<void>();
	private generatedAt = '';

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IAuditReportDialogData,
		private syncMonitoringService: SyncMonitoringService,
		private http: HttpClient,
		private notificationService: NotificationService,
	) {}

	ngOnInit(): void {
		this.poll(0);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	copy(): void {
		if (!this.reportText) {
			return;
		}
		navigator.clipboard?.writeText(this.reportText).then(
			() => this.notificationService.notify(['administration.sync-monitoring.report-dialog.copied'], [], 'success'),
			() => this.notificationService.notify(['administration.sync-monitoring.report-dialog.copy-failed'], [], 'error'),
		);
	}

	/** Saves the report read into the dialog; when it could not be read, the browser downloads it from the presigned URL. */
	save(): void {
		if (this.reportText) {
			downloadBlobFile(new Blob([this.reportText], { type: 'application/json' }), this.fileName());
		} else if (this.url) {
			downloadFile(this.url);
		}
	}

	private poll(attempt: number): void {
		this.syncMonitoringService
			.getAuditReport(this.data.environmentId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (report) => {
					if (report.url) {
						this.load(report.url);
					} else if (attempt + 1 < REPORT_POLL_LIMIT) {
						timer((report.retryAfter ?? 10) * 1000)
							.pipe(takeUntil(this.destroy$))
							.subscribe(() => this.poll(attempt + 1));
					} else {
						this.fail('errors.report-not-ready');
					}
				},
				error: (error: unknown) => this.fail(error instanceof HttpErrorResponse && error.status === 404 ? 'errors.report-no-audit' : 'errors.report-failed'),
			});
	}

	/** Reads the report from S3. If the browser may not read it (no CORS), the dialog still offers Save, which downloads it. */
	private load(url: string): void {
		this.url = url;
		this.http
			.get(url, { responseType: 'text' })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (text) => {
					this.reportText = text;
					this.describe(text);
					this.state = 'ready';
				},
				error: () => {
					this.errorKey = 'report-dialog.no-preview';
					this.state = 'ready';
				},
			});
	}

	private describe(text: string): void {
		this.preview = text.length > PREVIEW_MAX_LENGTH ? `${text.slice(0, PREVIEW_MAX_LENGTH)}\n…` : text;
		try {
			const report = JSON.parse(text) as { generatedAt?: string; tables?: { findings?: unknown[] }[] };
			const tables = report.tables ?? [];
			this.generatedAt = report.generatedAt ?? '';
			this.stats = {
				tables: tables.length,
				findings: tables.reduce((sum, table) => sum + (table.findings?.length ?? 0), 0),
				sizeKb: (new Blob([text]).size / 1024).toFixed(1),
			};
			if (text.length <= PREVIEW_MAX_LENGTH) {
				this.preview = JSON.stringify(report, null, 2);
			}
		} catch {
			// Not JSON: show it as it is.
		}
	}

	private fail(errorKey: string): void {
		this.errorKey = errorKey;
		this.state = 'failed';
	}

	private fileName(): string {
		const name = this.data.environmentName.replace(/[^\w-]+/g, '-').toLowerCase();
		const date = this.generatedAt.slice(0, 10) || new Date().toISOString().slice(0, 10);
		return `drift-audit-${name}-${date}.json`;
	}
}
